// v77.69 — About page (public).
//
// Structured, calm, professional. Matches the visual system of Landing.jsx
// (teal accents, soft surfaces, generous spacing, mobile-first).
//
// Public-only: NOT mounted under /app, so it never appears inside the
// authenticated app shell. Linked from the public Landing header / hamburger.

import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import LegalFooterInfo from "@/components/LegalFooterInfo";
import {
  ReceiptEuro,
  MessageSquare,
  Calendar,
  ShieldCheck,
  Scale,
  Stethoscope,
  HeartHandshake,
  Building2,
  Briefcase,
  Lock,
  ScrollText,
  ArrowRight,
  ArrowLeft,
} from "lucide-react";

// ---------- Reusable bits (mirror Landing.jsx tokens) ----------------------

const Eyebrow = ({ children }) => (
  <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#1F9FA4] dark:text-[#3FB8BC]">
    {children}
  </div>
);

const SectionHeading = ({ eyebrow, title, intro, center = false }) => (
  <div className={center ? "max-w-2xl mx-auto text-center" : "max-w-2xl"}>
    {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
    <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-3 text-[#1A2225] dark:text-[#F4F8F9]">
      {title}
    </h2>
    {intro && (
      <p className="text-base leading-relaxed mt-4 text-[#48595E] dark:text-[#A8B6BB]">
        {intro}
      </p>
    )}
  </div>
);

const cardSurface =
  "border border-[#E5EBEC] dark:border-[#2A3539] " +
  "bg-white dark:bg-[#161E21] " +
  "rounded-xl shadow-[0_1px_2px_rgba(15,21,23,0.04)] " +
  "hover:shadow-[0_8px_24px_-12px_rgba(31,159,164,0.18)] " +
  "transition-shadow duration-200";

const FeatureCard = ({ icon: Icon, title, desc, testid }) => (
  <div className={`${cardSurface} p-6`} data-testid={testid}>
    <div className="w-11 h-11 rounded-lg bg-[#E0F2F2] dark:bg-[#1F9FA4]/15 inline-flex items-center justify-center">
      <Icon className="w-5 h-5 text-[#116B6E] dark:text-[#3FB8BC]" strokeWidth={1.6} />
    </div>
    <h3 className="font-heading text-lg font-semibold mt-5 text-[#1A2225] dark:text-[#F4F8F9]">
      {title}
    </h3>
    <p className="text-sm leading-relaxed mt-2 text-[#48595E] dark:text-[#A8B6BB]">
      {desc}
    </p>
  </div>
);

const PrincipleBlock = ({ title, desc, testid }) => (
  <div className={`${cardSurface} p-6`} data-testid={testid}>
    <h3 className="font-heading text-base font-semibold text-[#1A2225] dark:text-[#F4F8F9]">
      {title}
    </h3>
    <p className="text-sm leading-relaxed mt-2 text-[#48595E] dark:text-[#A8B6BB]">
      {desc}
    </p>
  </div>
);

const ConsultRow = ({ icon: Icon, label, testid }) => (
  <li
    className="flex items-center gap-3 px-4 py-3 rounded-lg bg-white dark:bg-[#161E21] border border-[#E5EBEC] dark:border-[#2A3539]"
    data-testid={testid}
  >
    <span className="w-9 h-9 rounded-md bg-[#E0F2F2] dark:bg-[#1F9FA4]/15 inline-flex items-center justify-center flex-shrink-0">
      <Icon className="w-4 h-4 text-[#116B6E] dark:text-[#3FB8BC]" strokeWidth={1.7} />
    </span>
    <span className="text-sm text-[#1A2225] dark:text-[#E8EEF0] font-medium">
      {label}
    </span>
  </li>
);

// ---------- Page -----------------------------------------------------------

export default function About() {
  return (
    <div
      className="min-h-screen bg-white dark:bg-[#0E1416] text-[#1A2225] dark:text-[#E8EEF0]"
      data-testid="about-page"
    >
      {/* ------------------------------ HEADER ----------------------------- */}
      <header
        className="sticky top-0 z-30 border-b border-[#E5EBEC] dark:border-[#1F2A2D] bg-white/85 dark:bg-[#0E1416]/85 backdrop-blur"
        data-testid="about-header"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-14 sm:h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="about-brand-logo">
            <Logo size={32} />
            <span className="font-heading font-semibold text-base sm:text-lg tracking-tight text-[#1A2225] dark:text-[#F4F8F9]">
              CoParent Console
            </span>
          </Link>
          <Link
            to="/"
            className="text-sm text-[#48595E] dark:text-[#A8B6BB] hover:text-[#116B6E] dark:hover:text-[#3FB8BC] inline-flex items-center gap-1.5"
            data-testid="about-back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Home</span>
          </Link>
        </div>
      </header>

      {/* ------------------------------ 1. HERO ---------------------------- */}
      <section className="relative overflow-hidden" data-testid="about-hero">
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none bg-gradient-to-b from-[#E0F2F2]/60 via-white to-white dark:from-[#0F2F30]/70 dark:via-[#0E1416] dark:to-[#0E1416]"
        />
        <div
          aria-hidden="true"
          className="absolute -top-32 -left-32 w-[28rem] h-[28rem] rounded-full bg-[#1F9FA4]/10 dark:bg-[#1F9FA4]/15 blur-3xl pointer-events-none"
        />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 lg:pt-20 pb-12 sm:pb-16 text-center sm:text-left">
          <Eyebrow>About</Eyebrow>
          <h1 className="font-heading text-[2rem] leading-[1.15] sm:text-5xl lg:text-[3.25rem] lg:leading-[1.05] font-semibold tracking-tight mt-3 text-[#1A2225] dark:text-[#F4F8F9]">
            About CoParent Console
          </h1>
          <p className="mt-4 text-lg sm:text-xl text-[#116B6E] dark:text-[#3FB8BC] font-medium">
            Built for real-world co-parenting
          </p>
          <p className="mt-5 text-base leading-relaxed text-[#48595E] dark:text-[#B6C2C7] max-w-2xl mx-auto sm:mx-0">
            CoParent Console was created to bring structure, clarity and
            accountability to co-parenting.
          </p>
        </div>
      </section>

      {/* ------------------------------ 2. CONTEXT ------------------------- */}
      <section
        className="bg-white dark:bg-[#0E1416]"
        data-testid="about-context"
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-16">
          <SectionHeading
            eyebrow="Context"
            title="Co-parenting needs structure"
          />
          <div className="mt-7 space-y-5 text-base leading-relaxed text-[#2E3A3F] dark:text-[#C2CDD1]">
            <p>
              Family separation introduces practical challenges that require
              consistency and clear organisation. Managing shared expenses,
              maintaining communication, adhering to court or safety orders,
              and keeping accurate records can quickly become complex.
            </p>
            <p>
              Without a structured system, information becomes fragmented.
              Conversations are lost across channels, expenses are disputed,
              and important details are difficult to verify when needed.
            </p>
            <p className="text-[#1A2225] dark:text-[#F4F8F9] font-medium">
              CoParent Console was built to address these gaps.
            </p>
          </div>
        </div>
      </section>

      {/* ------------------- 3. STRUCTURED SOLUTION ------------------------ */}
      <section
        className="bg-[#FAFCFC] dark:bg-[#0B1011] border-y border-[#E5EBEC] dark:border-[#1F2A2D]"
        data-testid="about-solution"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <SectionHeading
            eyebrow="The platform"
            title="A clear, structured source of truth"
            intro="The platform provides a single, organised space where everything is recorded, time-stamped and easy to access."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 mt-10">
            <FeatureCard
              icon={ReceiptEuro}
              title="Expenses tracking"
              desc="Log and split costs clearly and fairly."
              testid="about-feat-expenses"
            />
            <FeatureCard
              icon={MessageSquare}
              title="Documented communication"
              desc="Searchable, time-stamped records."
              testid="about-feat-messaging"
            />
            <FeatureCard
              icon={Calendar}
              title="Shared schedules"
              desc="Transparent and easy to manage."
              testid="about-feat-schedules"
            />
            <FeatureCard
              icon={ShieldCheck}
              title="Secure records"
              desc="Consistent, reliable storage."
              testid="about-feat-records"
            />
          </div>
        </div>
      </section>

      {/* ----------------- 4. PROFESSIONAL VALIDATION ---------------------- */}
      <section
        className="bg-white dark:bg-[#0E1416]"
        data-testid="about-validation"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            <div className="lg:col-span-5">
              <SectionHeading
                eyebrow="Built with practitioners"
                title="Designed with real-world input"
                intro="CoParent Console has been developed in consultation with professionals working directly within the Irish system."
              />
            </div>
            <ul className="lg:col-span-7 grid sm:grid-cols-2 gap-3" data-testid="about-validation-list">
              <ConsultRow icon={ShieldCheck} label="Members of An Garda Síochána" testid="about-validation-garda" />
              <ConsultRow icon={Scale} label="Solicitors and legal professionals" testid="about-validation-solicitors" />
              <ConsultRow icon={Briefcase} label="Guardian ad Litems" testid="about-validation-gal" />
              <ConsultRow icon={Stethoscope} label="Counsellors and play therapists" testid="about-validation-counsellors" />
              <ConsultRow icon={HeartHandshake} label="Domestic violence support services" testid="about-validation-dv" />
              <ConsultRow icon={Building2} label="Tusla staff" testid="about-validation-tusla" />
            </ul>
          </div>
          <p className="mt-10 max-w-2xl text-base leading-relaxed text-[#48595E] dark:text-[#A8B6BB]">
            This ensures the platform reflects real-world needs, processes and
            challenges.
          </p>
        </div>
      </section>

      {/* ------------------------------ 5. PRINCIPLES ----------------------- */}
      <section
        className="bg-[#FAFCFC] dark:bg-[#0B1011] border-y border-[#E5EBEC] dark:border-[#1F2A2D]"
        data-testid="about-principles"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <SectionHeading
            eyebrow="Core principles"
            title="Calm. Neutral. Accountable."
          />
          <div className="grid sm:grid-cols-3 gap-4 sm:gap-5 mt-10">
            <PrincipleBlock
              title="Reduce ambiguity"
              desc="Clear structure across all interactions."
              testid="about-principle-ambiguity"
            />
            <PrincipleBlock
              title="Support fair communication"
              desc="Consistent, neutral environment."
              testid="about-principle-communication"
            />
            <PrincipleBlock
              title="Court-ready records"
              desc="Accurate, time-stamped documentation."
              testid="about-principle-records"
            />
          </div>
        </div>
      </section>

      {/* ------------------------------ 6. TRUST ---------------------------- */}
      <section
        className="bg-white dark:bg-[#0E1416]"
        data-testid="about-trust"
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="flex flex-col sm:flex-row items-start gap-5">
            <span className="w-11 h-11 rounded-lg bg-[#E0F2F2] dark:bg-[#1F9FA4]/15 inline-flex items-center justify-center flex-shrink-0">
              <Lock className="w-5 h-5 text-[#116B6E] dark:text-[#3FB8BC]" strokeWidth={1.7} />
            </span>
            <div className="min-w-0 flex-1">
              <Eyebrow>Trust &amp; privacy</Eyebrow>
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-3 text-[#1A2225] dark:text-[#F4F8F9]">
                Irish-built. Privacy-first.
              </h2>
              <div className="mt-5 space-y-4 text-base leading-relaxed text-[#48595E] dark:text-[#B6C2C7]">
                <p>
                  CoParent Console is an Irish-built platform, developed with
                  a strong focus on privacy, data protection and
                  child-centred design.
                </p>
                <p>
                  Information is organised per child, access is controlled,
                  and users decide what is shared and what remains private.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ----------------- 7. CLOSING STATEMENT ---------------------------- */}
      <section
        className="bg-gradient-to-b from-[#F4FAFA] to-white dark:from-[#0F2F30]/30 dark:to-[#0E1416] border-y border-[#E5EBEC] dark:border-[#1F2A2D]"
        data-testid="about-closing"
      >
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 text-center">
          <Eyebrow>Why it matters</Eyebrow>
          <div className="mt-6 space-y-3 text-lg sm:text-xl leading-relaxed text-[#1A2225] dark:text-[#F4F8F9] font-heading font-medium">
            <p>When communication breaks down, records matter.</p>
            <p>When agreements are disputed, clarity matters.</p>
            <p>When decisions affect children, accountability matters.</p>
          </div>
          <p className="mt-8 text-base text-[#48595E] dark:text-[#A8B6BB] max-w-xl mx-auto">
            CoParent Console provides a structured way forward.
          </p>
        </div>
      </section>

      {/* --------------- 7b. FOR PROFESSIONALS ----------------------------- */}
      <section
        className="bg-white dark:bg-[#0E1416]"
        data-testid="about-for-professionals"
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className={`${cardSurface} p-6 sm:p-8`}>
            <Eyebrow>For professionals</Eyebrow>
            <h3 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight mt-3 text-[#1A2225] dark:text-[#F4F8F9]">
              Solicitors, mediators, Tusla &amp; counsellors
            </h3>
            <p className="mt-4 text-base leading-relaxed text-[#48595E] dark:text-[#A8B6BB]">
              When a parent invites you in, you get clean, time-stamped,
              read-only access to the records they share — granter-led,
              per-child scoped, revocable at any time.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Link to="/professionals" data-testid="about-professionals-cta" className="w-full sm:w-auto">
                <Button className="w-full sm:w-auto bg-[#116B6E] hover:bg-[#0F5658] text-white rounded-md h-11 px-5">
                  Learn how access works
                  <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2} />
                </Button>
              </Link>
              <Link to="/contact" data-testid="about-professionals-contact" className="w-full sm:w-auto">
                <Button variant="outline" className="w-full sm:w-auto h-11 px-5 rounded-md border-[#D6F0EF] dark:border-[#2A3539]">
                  Get in touch
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ------------------------------ 8. CTA ----------------------------- */}
      <section
        className="relative bg-white dark:bg-[#0E1416] overflow-hidden"
        data-testid="about-cta"
      >
        <div
          aria-hidden="true"
          className="absolute -bottom-32 -right-24 w-[28rem] h-[28rem] rounded-full bg-[#1F9FA4]/10 dark:bg-[#1F9FA4]/15 blur-3xl pointer-events-none"
        />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight leading-[1.1] text-[#1A2225] dark:text-[#F4F8F9]">
            Start using CoParent Console today
          </h2>
          <div className="flex flex-col sm:flex-row gap-3 mt-8 justify-center">
            <Link
              to="/auth?mode=register"
              data-testid="about-cta-create"
              className="w-full sm:w-auto"
            >
              <Button className="w-full sm:w-auto bg-[#116B6E] hover:bg-[#0F5658] text-white rounded-md h-12 px-8 text-base font-medium shadow-sm transition-colors">
                Get started
                <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ------------------------------ FOOTER ----------------------------- */}
      <footer
        className="border-t border-[#E5EBEC] dark:border-[#1F2A2D] bg-[#FAFCFC] dark:bg-[#0B1011]"
        data-testid="about-footer"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Logo size={24} />
            <span className="font-heading font-semibold text-sm text-[#1A2225] dark:text-[#F4F8F9]">
              CoParent Console
            </span>
          </div>
          <p className="text-xs text-[#6F828A] dark:text-[#7A8A91] text-center">
            Proudly Irish-made and Irish-owned. Built with privacy, trust, and families in mind.
          </p>
          <div className="flex items-center gap-3 text-xs text-[#6F828A] dark:text-[#7A8A91]">
            <Link to="/about" className="hover:text-[#116B6E] dark:hover:text-[#3FB8BC]">About</Link>
            <span aria-hidden="true">·</span>
            <Link to="/privacy" className="hover:text-[#116B6E] dark:hover:text-[#3FB8BC]">Privacy</Link>
            <span aria-hidden="true">·</span>
            <Link to="/terms" className="hover:text-[#116B6E] dark:hover:text-[#3FB8BC]">Terms</Link>
            <span aria-hidden="true">·</span>
            <Link to="/security" className="hover:text-[#116B6E] dark:hover:text-[#3FB8BC]">Security</Link>
          </div>
        </div>
        <LegalFooterInfo />
      </footer>
    </div>
  );
}
